import { Middleware } from 'redux';
import {
    createOnlineOrder,
    createCodOrder,
    setFailure,
    PayStatus,
    transferStatus,
    WithPayState,
    CREATE_ORDER,
    OrderForm,
    InternalOrderForm,
    PAY_ORDER,
    getSourceInfo,
    setPayParams,
    PayParams,
    CHECK_QUEUE,
    START_CHECK_QUEUE,
    START_REQUEST_TOKEN,
    checkQueue,
    requestToken,
    STOP_CHECK_QUEUE,
    setQueueStatus,
    QueueStatus,
    setQueueTips,
    ExtraOrderInfo,
    createOrder
} from '../reducer';
import bridge from 'libs/bridge';
import urlParams from 'libs/query';
import { getContext } from '@tetris/ad.js';
import { isAd } from 'src/utils/ad';
import { IAppInfoExt } from 'types/bridge';
import openThenCloseSelf, { close } from 'utils/openlink-then-close-self';
import { sendEvent } from 'libs/event';
import { counter, log } from 'utils/slardar';
import { sendLogV1 } from 'libs/send-log';
import { getCouponIdObj } from 'libs/get-coupn-id';
import { getLiveEventParams, getMicroAppParams } from 'utils/helper';
import { toggleSupportRetIns } from 'pages/product/buynow/store/base-reducer';
import env from 'libs/env';
import toast from 'components/common/toast';
import { DOUYIN_ORDER_COLLECT } from 'services/v2/order/const';
import { renderRiskSDK } from 'utils/risk-control';

const pvTime = new Date().getTime();
export function getAdInfo() {
    return new Promise((resolve, reject) => {
        if (urlParams.getAdInfoLocal) {
            const json = JSON.parse(localStorage.getItem('LOCAL_AD_INFO') || '[]');
            resolve(json);
        } else {
            setTimeout(() => reject(new Error('time out')), 1000);
            Promise.all([getContext(), bridge.call('adInfo')])
                .then((res) => resolve(res))
                .catch((err) => reject(err));
        }
    });
}

export interface ICreateOrderResp {
    st: number;
    data: PayParams;
    msg: string;
}

/**
 * 用于处理`CreateOrderAction`。需要使用者提供一个Middleware于该Middleware前，
 * 用于补全`CreateOrderAction`的payload，结构为`OrderForm`。
 * @param store 应用该Middleware的store
 */
const orderMiddleware: Middleware<{}, WithPayState> = (store) => {
    const { dispatch, getState } = store;
    let overTimeLock = false;
    // 整个排队的timer，超时后失败并停止
    let overTimer: number | undefined;
    // 每次轮询的timer，手动取消订单后需要clear
    let checkTimer: number | undefined;
    // 延迟展示排队的timer，可能用户已经进入队列但是延迟排队组件展示
    let showQueueModalTimer: number | undefined;
    let pollCounter = 0;
    let startDisplayQueueTime = 0;
    let createOrderExtraInfo: ExtraOrderInfo;
    const QUEUE_DISPLAY_DELAY = 1500;
    const CHECK_GAP = 500;
    const CHECK_COUNT = 4;
    // 五分十秒取消
    const FINISH_QUEUE_TIME = 1000 * 60 * 5 + 1000 * 10;

    const liveEventParams = getLiveEventParams();
    const microAppParams = getMicroAppParams();

    let token = '';
    let requestTokenTimer: number;
    let requestTokenCounter = 0;

    const payHandler = (res: ICreateOrderResp, finalStatus: PayStatus) => {
        const orderId =
            res.data &&
            (res.data.order_id || (Array.isArray(res.data.orderIds) && res.data.orderIds[0]));
        const { shop, platform } = (getState().coupon && getState().coupon.selected) || {
            shop: {},
            platform: {}
        };
        const couponIdObj = getCouponIdObj({ shop, platform });
        if (res.st === 0 || res.st === 20403) {
            sendEvent('order_submit_success', {
                order_id: orderId,
                ...couponIdObj,
                ...getLiveEventParams(),
                ...liveEventParams
            });
            if (isAd()) {
                // 鲁班第一版埋点
                sendLogV1();
            }
        }
        switch (res.st) {
            /**
             * code含义：
             * 20500：退货运费险 被商家端取消 需要重新提交订单
             * 20402：需要排队支付
             * 20403：支付金额超限
             * 20204: 命中处罚 【全部实名合作方均需要经过合规审查】
             * 20600: 下单失败 【命中黄牛风控】需要提供 token
             * 20601: 下单失败，delay token验证失败
             * 0：生单成功
             */
            case 20500:
                dispatch(toggleSupportRetIns(false));
                transferStatus(dispatch, getState().pay.payStatus, PayStatus.OrderFail);
                return dispatch({
                    type: 'TOGGLE_RET_INS_DIALOG',
                    payload: { retInsDialogContent: res.msg, retInsDialogShow: true }
                });
            case 20402:
                if (getState().pay.payStatus !== PayStatus.Queue) {
                    dispatch(setPayParams(res.data));
                    dispatch(setQueueTips(res.msg || '支付人数过多，请耐心等待，不要走开哦'));
                    transferStatus(dispatch, getState().pay.payStatus, PayStatus.Queue);
                    return dispatch({ type: START_CHECK_QUEUE, payload: true });
                }
                return dispatch({ type: CHECK_QUEUE, payload: true });
            case 20600:
                if (getState().pay.payStatus !== PayStatus.WaitingToken) {
                    transferStatus(dispatch, getState().pay.payStatus, PayStatus.WaitingToken);
                    token = '';
                    return dispatch({ type: START_REQUEST_TOKEN });
                }
                break;
            case 20403:
                dispatch(setPayParams(res.data));
                dispatch(setFailure({ code: res.st, message: res.msg }));
                transferStatus(dispatch, getState().pay.payStatus, PayStatus.Paying);
                transferStatus(dispatch, getState().pay.payStatus, PayStatus.Fail);
                break;
            case 20205:
            case 20206:
            case 20207:
            case 20208:
                transferStatus(dispatch, getState().pay.payStatus, PayStatus.OrderFail);
                renderRiskSDK({
                    code: getState().pay?.failure?.code ?? '',
                    callback: () => {
                        dispatch(createOrder());
                    },
                    error: (err: string) => {
                        toast(err || '系统异常，请重试');
                    }
                });
                break;
            case 0: {
                dispatch(setPayParams(res.data));
                const now = new Date().getTime();
                const timeDiff = now - startDisplayQueueTime;
                const timeout = timeDiff > 0 && timeDiff < 1000 ? 1000 - timeDiff : 0;
                setTimeout(() => {
                    dispatch({
                        type: PAY_ORDER,
                        payload: {
                            id: orderId
                        }
                    });
                }, timeout);
                break;
            }
            default:
                token = '';
                sendEvent('pay_fail', { stage: 1, extra: res.data });
                transferStatus(dispatch, getState().pay.payStatus, finalStatus);
                return dispatch(setFailure({ code: res.st, message: res.msg }));
        }
    };

    // eslint-disable-next-line complexity
    return (next) => (action) => {
        // 结束轮询，状态转为ready
        if (action.type && action.type === STOP_CHECK_QUEUE) {
            overTimeLock = true;
            clearTimeout(overTimer);
            clearTimeout(checkTimer);
            transferStatus(dispatch, getState().pay.payStatus, PayStatus.Ready);
        }
        // 轮询风控排队 5 秒，获取 下单 token
        if (action.type && action.type === START_REQUEST_TOKEN) {
            const getToken = () => {
                requestTokenTimer = window.setTimeout(() => {
                    if (requestTokenCounter > 5) {
                        clearInterval(requestTokenTimer);
                        requestTokenCounter = 0;
                        token = '';
                        transferStatus(dispatch, getState().pay.payStatus, PayStatus.OrderFail);
                        return dispatch(
                            setFailure({ code: 20601, message: '当前下单人数过多，请稍后重试' })
                        );
                    }

                    requestToken().then((res) => {
                        const data = res ? res.data : {};
                        if (data.token) {
                            transferStatus(dispatch, getState().pay.payStatus, PayStatus.Ready);
                            clearTimeout(requestTokenTimer);
                            requestTokenCounter = 0;
                            token = data.token;
                            dispatch(createOrder());
                        } else {
                            getToken();
                        }
                    });
                    requestTokenCounter += 1;
                }, 1000);
            };
            getToken();
        }
        // 开始轮询
        if (action.type && action.type === START_CHECK_QUEUE) {
            const payParams = getState().pay.payParams;
            const orderId = payParams && payParams.order_id;
            if (!orderId)
                return transferStatus(dispatch, getState().pay.payStatus, PayStatus.OrderFail);

            // 开始check时需要重置timer，否则取消后二次支付会出现问题
            overTimeLock = false;
            overTimer = undefined;
            showQueueModalTimer = undefined;
            pollCounter = 0;
            startDisplayQueueTime = 0;
            dispatch({ type: CHECK_QUEUE });
            dispatch(setQueueStatus(QueueStatus.QUEUING_SILENCE));
        }
        // 单次轮询
        if (action.type && action.type === CHECK_QUEUE) {
            const payState = getState().pay;
            const payParams = payState.payParams;
            const payType = payState.chosenPayMethod ? payState.chosenPayMethod.payType : 0;
            const orderId = payParams && payParams.order_id;
            if (!orderId) return next(action);
            if (!overTimer) {
                overTimer = window.setTimeout(() => {
                    overTimeLock = true;
                    dispatch(setQueueStatus(QueueStatus.TIMEOUT));
                    startDisplayQueueTime = new Date().getTime();
                }, FINISH_QUEUE_TIME);
            }
            if (!showQueueModalTimer) {
                showQueueModalTimer = window.setTimeout(() => {
                    dispatch(setQueueStatus(QueueStatus.QUEUING));
                }, QUEUE_DISPLAY_DELAY);
            }
            const gap = pollCounter < CHECK_COUNT ? CHECK_GAP : CHECK_GAP * CHECK_COUNT;
            checkTimer = window.setTimeout(() => {
                if (!overTimeLock) {
                    // 在购物车提单的场景下check支付方式应该用outTradeNo，因为可能存在多个订单
                    const checkId =
                        createOrderExtraInfo &&
                        createOrderExtraInfo.isBatchOrder &&
                        payParams &&
                        payParams.outTradeNo
                            ? payParams.outTradeNo
                            : orderId;
                    checkQueue(checkId, orderId, payType, ++pollCounter).then(
                        (response: { data: ICreateOrderResp }) => {
                            // 确保没有stop
                            !overTimeLock && payHandler(response.data, PayStatus.Fail);
                        }
                    );
                } else {
                    transferStatus(dispatch, getState().pay.payStatus, PayStatus.Fail);
                }
            }, gap);
        }

        if (action.type && action.type === CREATE_ORDER) {
            if (action.payload && action.payload.postForm) {
                createOrderExtraInfo = action.payload.extraInfo;
                const exceedCodOrder = (data: InternalOrderForm) => {
                    return createCodOrder(data)
                        .then((response) => {
                            const { shop, platform } = getState().coupon.selected || {
                                shop: {},
                                platform: {}
                            };
                            const couponIdObj = getCouponIdObj({ shop, platform });
                            const res = response.data;
                            if (res.st === 0) {
                                sendEvent('order_submit_success', {
                                    order_id: res.data.order_id,
                                    ...couponIdObj,
                                    ...getLiveEventParams(),
                                    ...liveEventParams
                                });
                                if (isAd()) {
                                    // 鲁班第一版埋点
                                    sendLogV1();
                                }
                                dispatch(setPayParams(res.data));
                                return transferStatus(
                                    dispatch,
                                    getState().pay.payStatus,
                                    PayStatus.Success
                                );
                            } else {
                                transferStatus(
                                    dispatch,
                                    getState().pay.payStatus,
                                    PayStatus.OrderFail
                                );
                                return dispatch(setFailure({ code: res.st, message: res.msg }));
                            }
                        })
                        .catch((err) => {
                            transferStatus(dispatch, getState().pay.payStatus, PayStatus.OrderFail);

                            // 提单接口失败原因观察
                            counter('error_order_submit', {
                                tags: {
                                    type: 'CodOrder',
                                    error: err.msg || err.message || 'unknown'
                                }
                            });

                            return dispatch(
                                setFailure({ code: 10000, message: '下单失败，请稍后重试' })
                            );
                        });
                };
                const state = getState();

                const exceedOnlineOrder = (data: InternalOrderForm) => {
                    createOnlineOrder(data, createOrderExtraInfo)
                        .then((response) => {
                            const res = response.data;
                            // 需要拆单支付直接跳走
                            if (
                                createOrderExtraInfo.isBatchOrder &&
                                getState().pay.needSplitOrder &&
                                res.st === 0
                            ) {
                                if (env.isInAweme) {
                                    toast('此订单不支持合并支付，请前往订单列表分别支付');
                                    return setTimeout(() => {
                                        bridge.call('openSchema', {
                                            schema: DOUYIN_ORDER_COLLECT
                                        });
                                        setTimeout(() => {
                                            close();
                                        }, 300);
                                    }, 1000);
                                } else {
                                    return openThenCloseSelf(
                                        `/views/neworder/center?confirm_text=${encodeURIComponent(
                                            '此订单不支持合并支付，请前往订单列表分别支付'
                                        )}`,
                                        { control: true }
                                    );
                                }
                            }
                            return payHandler(res, PayStatus.OrderFail);
                        })
                        .catch((err) => {
                            sendEvent('pay_fail', { stage: 1, extra: err });

                            transferStatus(dispatch, getState().pay.payStatus, PayStatus.OrderFail);

                            // 提单接口失败原因观察
                            counter('error_order_submit', {
                                tags: {
                                    type: 'OnlineOrder',
                                    error: err.msg || err.message || 'unknown'
                                }
                            });

                            return dispatch(
                                setFailure({ code: 10000, message: '下单失败，请稍后重试' })
                            );
                        });
                };

                const payStatus = state.pay.payStatus;
                const chosenPayMethod = state.pay.chosenPayMethod;
                // 只有拆单支付允许不上报支付方式
                if (!chosenPayMethod && !state.pay.needSplitOrder) return;
                if (!transferStatus(dispatch, payStatus, PayStatus.CreatingOrder)) return;
                const formData = action.payload.postForm as OrderForm;
                const data: InternalOrderForm = {
                    ...getSourceInfo(),
                    ...formData,
                    ...(chosenPayMethod && { sub_way: chosenPayMethod.subWay }),
                    ...(chosenPayMethod && { pay_type: chosenPayMethod.payType }),
                    token,
                    s_type: liveEventParams.live_room_id ? '1' : '',
                    room_id: liveEventParams.live_room_id || '',
                    author_id: liveEventParams.live_author_id || formData.author_id || '',
                    sub_b_type: microAppParams && microAppParams.appId ? '2' : '3',
                    open_id: microAppParams && microAppParams.openId
                };

                getAdInfo()
                    .then((res: any) => {
                        // dou+转化
                        let [adData, adInfoData] = res;
                        if (adData && adData.cid && +Number(adData.cid)) {
                            const extra: any = {
                                ad_request: JSON.stringify(adData),
                                properties: JSON.stringify({
                                    duration: new Date().getTime() - pvTime,
                                    come_from: urlParams.come_from || '0'
                                }),
                                fe_params: adInfoData.ad_extra_data || '{}'
                            };
                            data.source_id = (adData.cid || '0').toString();
                            data.new_source_id = data.source_id;
                            // 广告创意id，目前和new_source_id以及source_id一致。。
                            data.cid = data.source_id;
                            if (isAd()) {
                                extra.event_type = 'shopping';
                                extra.event_value = '';

                                data.origin_id = data.source_id;
                                data.source_type =
                                    data.source_type === '0'
                                        ? urlParams.origin_type
                                        : data.source_type;
                                try {
                                    const extra = JSON.parse(data.extra || '{}') || {};
                                    const log_extra = JSON.parse(adData.log_extra);
                                    data.extra = JSON.stringify(Object.assign(extra, log_extra));
                                } catch (e) {
                                    data.extra = '';
                                }
                            }
                            data.report_event = JSON.stringify(extra);
                        }
                    })
                    .finally(() => {
                        if (chosenPayMethod && chosenPayMethod.payType === 0) {
                            return exceedCodOrder(data);
                        } else {
                            return exceedOnlineOrder(data);
                        }
                    })
                    .catch((err) => {
                        console.error(err);
                        // 提单失败
                        log(err, {
                            type: 'error_order_submit',
                            payType: String(
                                (chosenPayMethod && chosenPayMethod.payType) || 'unknown'
                            )
                        });
                    });
            }
        }
        return next(action);
    };
};

export default orderMiddleware;
